import cn from 'classnames'
import SingleLineTextField from 'shared/components/input/SingleLineTextField'
import { ChangeEventHandler, KeyboardEvent, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { TagCloud } from 'react-tagcloud'
import { IndividualFindArtFormData } from '../../types'

type RoomType = {
  value: string
  label: string
  isOther?: true
}

const ROOM_TYPES: RoomType[] = [
  {
    label: 'Bedroom',
    value: 'Bedroom',
  },
  {
    label: 'Living Room',
    value: 'Living Room',
  },
  {
    label: 'Home Office',
    value: 'Home Office',
  },
  {
    label: 'Kitchen',
    value: 'Kitchen',
  },
  {
    label: 'Dining Room',
    value: 'Dining Room',
  },
  {
    label: 'Entryway',
    value: 'Entryway',
  },
  {
    label: 'Hallway',
    value: 'Hallway',
  },
  {
    label: 'Other (specify)',
    value: 'other',
    isOther: true,
  },
]

const RoomTypeStep = ({ onNext }: { onNext: () => void }) => {
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<Pick<IndividualFindArtFormData, 'isOtherRoomType' | 'roomType'>>()
  const selectedRoomType = watch('roomType')
  const isSelectingOtherRoomType = watch('isOtherRoomType')

  const inputRef = useRef<HTMLInputElement>(null)

  const handleToggleRoomType = (roomType: RoomType) => {
    if (roomType.value === selectedRoomType) return // already selected
    const isSelectingOther = roomType.value === 'other'
    if (isSelectingOther) {
      // focus on the input when selecting other
      setTimeout(() => {
        inputRef.current?.focus()
      }, 150)
    }

    if (isSelectingOtherRoomType && isSelectingOther) return // already selecting other
    setValue('isOtherRoomType', isSelectingOther)
    setValue('roomType', isSelectingOther ? '' : roomType.value) // clear the value if selecting other, so the input is empty
    trigger('roomType')
    if (!isSelectingOther) onNext() // move to next step if not selecting other
  }

  const changeCustomRoomType: ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue('roomType', e.target.value)
    trigger('roomType')
  }

  const handleOnNext = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation() // Prevents event bubbling
      e.preventDefault() // Prevents the default form submission
      onNext()
    }
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-6 px-4 text-kokushoku-black lg:gap-12 lg:px-[12px]">
      <div className="text-center text-[25px] font-semibold leading-[1.25] lg:text-[36px]">Where do you need art?</div>
      <div className="w-full max-w-[800px]">
        <TagCloud
          shuffle={false}
          minSize={12}
          maxSize={35}
          tags={ROOM_TYPES}
          className="-lg:translate-x-4 -translate-x-2 space-x-2 space-y-2 text-center lg:space-x-4 lg:space-y-4"
          renderer={({ value, label }: RoomType) => {
            const isSelected = selectedRoomType === value || (value === 'other' && isSelectingOtherRoomType)

            return (
              // need to wrap in a div to be able to use TagCloud's onClick event handler
              <div
                className={cn(
                  'inline-flex cursor-pointer rounded-full px-4 py-4 lg:px-6',
                  isSelected
                    ? 'border-[3px] border-primary text-primary'
                    : 'border border-black/10 text-kokushoku-black',
                  'transition-all hover:border-primary hover:text-primary hover:shadow',
                )}
                key={value}
                role="button"
              >
                <span className="text-[14px] leading-[1.25] lg:text-[20px]">{label}</span>
              </div>
            )
          }}
          onClick={handleToggleRoomType}
        />
      </div>
      <div className={cn('w-full max-w-[600px]', !isSelectingOtherRoomType && 'hidden')}>
        <SingleLineTextField
          textSize="secondary"
          footnote="Specify where you are looking to get art for, this could be “library”, “playroom”, etc."
          inputRef={inputRef}
          value={isSelectingOtherRoomType ? selectedRoomType : ''}
          onChange={changeCustomRoomType}
          error={selectedRoomType === '' ? undefined : errors.roomType?.message} // don't show error if the value is empty
          inputClass="!p-4"
          onKeyDown={handleOnNext}
        />
      </div>
    </div>
  )
}

export default RoomTypeStep
